import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { TableColumn } from '../../atoms/table/table.model';
import { TableComponent } from '../../atoms/table/table.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  TranslocoDecimalPipe,
} from '@jsverse/transloco-locale';
import { ADMIN_TABLE_DEFAULT_COLUMNS } from '../../../constants/table.constants';
import { Unit } from '../../../models/units.models';
import { UNITS } from '../../../constants/units.constants';
import { ActionsIndicatorComponent } from '../../molecules/indicators/actions-indicator.component';
import { IndicatorAction } from '@ui';
import { AdministrationCenterIndicatorComponent } from '../../molecules/indicators/administration-center-indicator.component';
import { ActionsSwitchIndicatorComponent } from '../../molecules/indicators/actions-switch-indicator.component';
import { AdministrationTrafoIndicatorComponent } from "../../molecules/indicators/administration-trafo-indicator.component";
import { AdministrationLineIndicatorComponent } from "../../molecules/indicators/administration-line-indicator.component";
import { PermissionsService } from '../../../../app/core/services/permissions.service';

@Component({
  selector: 'ui-administration-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableComponent,
    TranslocoDirective,
    TranslocoDecimalPipe,
    AdministrationCenterIndicatorComponent,
    ActionsSwitchIndicatorComponent,
    ActionsIndicatorComponent,
    AdministrationTrafoIndicatorComponent,
    AdministrationLineIndicatorComponent
],
  template: `
    <ng-container *transloco="let t">
      @if (dataSource() && columns().length) {
        <ui-table
          [dataSource]="dataSource()"
          [columns]="columns()"
          [columnTemplates]="columnTemplates()"
          [scrollConfig]="scrollConfig()"
          [pageSize]="pageSize()"
          [pageIndex]="pageIndex()"
          [showPagination]="showPagination()"
          [showPageSizeChanger]="showPageSizeChanger()"
          [total]="total()"
          [showTotal]="total() ? totalTemplate : null"
          [loading]="loading()"
          [frontPagination]="false"
          [isMultiSelect]="true"
          (onQueryParams)="onQueryParamsChange($event)"
          (onChecked)="handleChecked($event)"
        />
      }

      <ng-template #centerTemplate let-row="row">
      @if(row.centerName && row.centerId){
        <ui-administration-center-indicator
          [name]="row.centerName"
          [centerId]="row.centerId"
          [distributorId]="row.distributorCode"
          [rsabtId]="row.rsabtId"
          [tsabtId]="row.tsabtId"
          [vtnId]="row.vtnId"
          (onCenter)="emitCenter($event)"
        />
      }
      @else {
      ---
      }
      </ng-template>
      <ng-template #transformerTemplate let-row="row">
         @if(row.transformerName && row.transformerId){
           <ui-administration-trafo-indicator
             [name]="row.transformerName"
             [transformerId]="row.transformerId"
             [distributorId]="row.distributorId"
             [rsabtId]="row.rsabtId"
             [tsabtId]="row.tsabtId"
             [vtnId]="row.vtnId"
           />
         }
         @else {
          ---
         }
      </ng-template>
      <ng-template #lineTemplate let-row="row">
          <ui-administration-line-indicator
             [name]="row.lineName"
             [lineId]="row.lineId"
             [distributorId]="row.distributorId"
             [rsabtId]="row.rsabtId"
             [tsabtId]="row.tsabtId"
             [vtnId]="row.vtnId"
           />
      </ng-template>
      <ng-template #customizationTemplate let-row="row">
        @if (hasWritePermission()) {
          <div class="flex justify-end">
            <ui-actions-switch-indicator
              (onAction)="emitActionSwitch(row)"
              [switchValue]="row.enabled"
            />
          </div>
        }
      </ng-template>
      <ng-template #actionsTemplate let-row="row">
        <div class="flex justify-end gap-x-3">
          @if (hasWritePermission()) {
            <ui-actions-indicator
              (onAction)="emitActionEdit(row)"
              [actions]="{
                edit: { disabled: false },
              }"
            />
          }
          @if (hasDeletePermission()) {
            <ui-actions-indicator
              (onAction)="emitActionDelete(row)"
              [actions]="{
                delete: { disabled: false },
              }"
            />
          }
          <ui-actions-indicator
            (onAction)="emitActionView(row)"
            [actions]="{
              view: { disabled: false },
            }"
          />
        </div>
      </ng-template>

      <ng-template #totalTemplate>
        {{ total() | translocoDecimal }}
        {{ t('DISTRIBUTION_NETWORK.TABLE.RESULTS') }}
      </ng-template>
    </ng-container>
  `,
})
export class AdministrationTableComponent {
  private translocoService = inject(TranslocoService);
  private destroyRef = inject(DestroyRef);
  protected readonly permissionsService = inject(PermissionsService);

  data = input.required<any[]>();
  total = input<number>(100);
  loading = input<boolean>(false);
  pageSize = input<number>(10);
  pageIndex = input<number>(1);
  showPagination = input<boolean>(true);
  showPageSizeChanger = input<boolean>(true);
  showCenterCode = input<boolean>(false);
  deviceType = input<string>('');

  onQueryParams = output<{
    orderBy: string | undefined;
    order: string | undefined;
    pageSize: number;
    pageIndex: number;
  }>();
  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }>();
  onCopyCenterId = output<string>();
  onRsabtId = output<string>();
  onAction = output<{indicator: IndicatorAction, element: any}>();
  onChecked = output<any[]>();

  actions = signal<Record<any, { disabled: boolean }>>({
    archive: { disabled: false },
  });

  dataSource = computed(() =>
    this.data().map((item) => ({ ...item })),
  );

  centerTemplate = viewChild.required<TemplateRef<any>>('centerTemplate');
  transformerTemplate = viewChild.required<TemplateRef<any>>('transformerTemplate');
  lineTemplate = viewChild.required<TemplateRef<any>>('lineTemplate');
  customizationTemplate = viewChild.required<TemplateRef<any>>('customizationTemplate');
  actionsTemplate = viewChild.required<TemplateRef<any>>('actionsTemplate');

  columnTemplates = computed(() => ({
    center: this.centerTemplate(),
    transformer: this.transformerTemplate(),
    line: this.lineTemplate(),
    customization: this.customizationTemplate(),
    actions: this.actionsTemplate(),
  }));
  columns = signal<TableColumn[]>([]);
  scrollConfigDefault = signal<{ x?: string | null; y?: string | null }>({
    x: '1000px',
    y: 'calc(100vh - 350px)',
  });
  scrollConfig = computed<{ x?: string | null; y?: string | null }>(() =>
     this.data()?.length ? this.scrollConfigDefault() : {},
  );
  DEFAULT_COLUMNS = computed<TableColumn[]>(() => {
    let cols = ADMIN_TABLE_DEFAULT_COLUMNS;
    // If the device type is not 'tsabt', remove the 'line' column
    if (this.deviceType() !== 'tsabt') {
      cols = cols.filter(col => col.key !== 'line');
    }
    // If the user does not have write permissions, remove 'customization'
    if (!this.hasWritePermission()) {
      cols = cols.filter(col => col.key !== 'customization');
    }
    return cols;
  });
  permissions = this.permissionsService.permissions;

  /**
   * Initializes the AdministrationTableComponent and sets up effects for columns and localization.
   */
  constructor() {
    
    effect(() => {
      this.setLocalizeColumns();
    });
  }


  /**
   * Returns true if the user has 'w' write permission.
   */
  hasWritePermission(): boolean {
    return this.permissions().some(p => p === 'sabt:alarm_cfg:w');
  }

  /**
   * Returns true if the user has 'd' delete permission.
   */
  hasDeletePermission(): boolean {
    return this.permissions().some(p => p === 'sabt:alarm_cfg:d');
  }
  /**
   * Sets the localized column titles using the current translation.
   */
  setLocalizeColumns() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns.set(
          this.DEFAULT_COLUMNS().map((col) => ({
            ...col,
            title: this.translocoService.translate(col.title),
          })),
        );
      });
  }

  /**
   * Emits the selected RSABT ID.
   * @param id - The RSABT ID to emit.
   */
  emitRsabtId(id: string) {
    this.onRsabtId.emit(id);
  }

  /**
   * Handles changes to table query parameters and emits them.
   * @param params - The table query parameters.
   */
  onQueryParamsChange(params: NzTableQueryParams): void {
    const { sort, pageSize, pageIndex } = params;
    const currentSort = sort.find((item) => item.value !== null);
    const orderBy = (currentSort && currentSort.key) || undefined;
    const order = (currentSort && currentSort.value) || undefined;
    this.onQueryParams.emit({ orderBy, order, pageSize, pageIndex });
  }

  /**
   * Emits the selected center event.
   * @param event - The center event object containing IDs.
   */
  emitCenter(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }) {
    this.onCenter.emit(event);
  }

  /**
   * Returns the unit label for a given unit key.
   * @param unit - The unit key.
   * @returns The unit label.
   */
  getUnit(unit: Unit) {
    return UNITS[unit];
  }

  /**
   * Returns the actions object for a table item, setting the archive action's disabled state.
   * @param disabled - Whether the archive action should be disabled.
   * @returns The actions object.
   */
  getItemActions(disabled: boolean): Record<any, { disabled: boolean }> {
    return { archive: { disabled } };
  }

  /**
   * Emits the edit action for a table row.
   * @param action - The indicator action object.
   */
  emitActionEdit(action: IndicatorAction) {
    this.onAction.emit({indicator: 'edit', element: action});
  }

  /**
   * Emits the edit action for a table row.
   * @param action - The indicator action object.
   */
  emitActionView(action: IndicatorAction) {
    this.onAction.emit({indicator: 'view-saved', element: action});
  }

  /**
   * Emits the delete action for a table row.
   * @param action - The indicator action object.
   */
  emitActionDelete(action: IndicatorAction) {
    this.onAction.emit({indicator: 'delete', element: action});
  }

  /**
   * Emits the archive (switch) action for a table row.
   * @param action - The indicator action object.
   */
  emitActionSwitch(action: IndicatorAction) {
    this.onAction.emit({indicator: 'archive', element: action});
  }

  /**
   * Emits the checked row indices.
   * @param indexs - Array of checked row indices.
   */
  handleChecked(indexs: number[]) {
    this.onChecked.emit(indexs);
  }

  /**
   * Returns the active state based on the read flag.
   * @param read - Boolean indicating if the row is read.
   * @returns True if not read, false otherwise.
   */
  getActiveState(read:boolean) {
    return  !read === true ? true : false;
  }
}
